import React, { createContext, useContext, useEffect, useState } from 'react';
import ApiService from 'services/apiService';
import { StudentDashboardData } from '../types';

interface StudentDataContextValue {
  data: StudentDashboardData | null;
  loading: boolean;
  error: string | null;
}

const StudentDataContext = createContext<StudentDataContextValue | undefined>(undefined);

interface StudentDataProviderProps {
  children: React.ReactNode;
  schoolYear: number;
}

export const StudentDataProvider: React.FC<StudentDataProviderProps> = ({ 
  children,
  schoolYear
}) => {
  const [data, setData] = useState<StudentDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await ApiService.home.getStudentCountsByGrade(schoolYear);

        setData({
          rows: (response.data ?? []).map(item => ({
            gradeLevelId: item.gradeLevelId ?? 0,
            gradeLevelName: item.gradeLevelName ?? '',
            studentCount: item.studentCount ?? 0,
          })),
          total: response.total ?? 0,
          schoolYear: response.schoolYear ?? schoolYear,
          schoolYearDisplay: response.schoolYearDisplay ?? `${schoolYear}-${(schoolYear + 1).toString().slice(2)}`,
        });
      } catch (err) {
        console.error('Failed to fetch student counts by grade:', err);
        setError('Failed to load student data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolYear]);

  return (
    <StudentDataContext.Provider value={{ data, loading, error }}>
      {children}
    </StudentDataContext.Provider>
  );
};

export const useStudentData = (): StudentDataContextValue => {
  const context = useContext(StudentDataContext);
  if (!context) {
    throw new Error('useStudentData must be used within StudentDataProvider');
  }
  return context;
};